/**
 * Nível 1 nav (nav refactor, SPEC_nav_menu_refactor.md; Map merged into
 * Search, spec-search-map-merge.md): Local | Following | Groups (icon +
 * label) then Search (icon only). Local/Following are feed "lenses"
 * (unchanged behavior); Groups/Search navigate away to their own pages.
 *
 * Nível 2 (post-type filter chips) lives in FeedTypeChips, rendered by
 * Home.tsx below this bar — only meaningful for the Local/Following lens.
 */
import { Search, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useFeedStore } from '@/store/feed.store'
import { useAuthStore } from '@/store/auth.store'
import type { FeedType } from '@olimhub/shared'

const LENS_TABS: Array<{ key: FeedType; labelKey: string; requiresAuth?: boolean }> = [
  { key: 'local', labelKey: 'feed.local' },
  { key: 'following', labelKey: 'feed.following', requiresAuth: true },
]

export function FeedBar() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { selectedFeed, setFeed } = useFeedStore()
  const { isAuthenticated } = useAuthStore()
  const isHome = location.pathname === '/'

  const activeTag = searchParams.get('tag') ?? ''

  function clearTag() {
    setSearchParams((prev) => { prev.delete('tag'); return prev })
  }

  function goAuthed(path: string) {
    if (!isAuthenticated) {
      navigate(`/login?from=${encodeURIComponent(path)}`)
      return
    }
    navigate(path)
  }

  return (
    <div
      style={{
        flexShrink: 0,
        borderBottom: '1px solid var(--border)',
        background: 'var(--surface)',
        position: 'sticky',
        top: 0,
        zIndex: 'var(--z-header)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'stretch', height: 'var(--header-tabs)' }}>
        {LENS_TABS.filter(({ requiresAuth }) => !requiresAuth || isAuthenticated).map(({ key, labelKey, requiresAuth }) => {
          // Active only while actually on Home — otherwise Local could read as
          // "selected" while sitting on Map/Groups/Search just because it was
          // the last lens chosen. Clicking always routes back to '/' too, so
          // the tab works as real navigation from any other Nível-1 screen,
          // not just a filter that only takes effect if you're already Home.
          const active = isHome && selectedFeed === key
          function handleClick() {
            if (requiresAuth && !isAuthenticated) {
              navigate(`/login?from=${encodeURIComponent('/')}`)
              return
            }
            setFeed(key)
            navigate('/')
          }
          return (
            <button
              key={key}
              onClick={handleClick}
              aria-current={active}
              style={{
                flex: 1,
                minWidth: 0,
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                position: 'relative',
                fontFamily: 'var(--font-sans)',
                fontWeight: active ? 700 : 500,
                fontSize: 'var(--fs-xs)',
                color: active ? 'var(--text-strong)' : 'var(--text-muted)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                padding: '0 4px',
                transition: 'color var(--dur-fast) var(--ease-out)',
              }}
            >
              {t(labelKey)}
              {active && (
                <span
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 28,
                    height: 3,
                    borderRadius: 3,
                    background: 'var(--brand)',
                  }}
                />
              )}
            </button>
          )
        })}

        <button
          onClick={() => goAuthed('/groups')}
          aria-current={location.pathname === '/groups'}
          style={{
            flex: 1,
            minWidth: 0,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            position: 'relative',
            fontFamily: 'var(--font-sans)',
            fontWeight: location.pathname === '/groups' ? 700 : 500,
            fontSize: 'var(--fs-xs)',
            color: location.pathname === '/groups' ? 'var(--text-strong)' : 'var(--text-muted)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            padding: '0 4px',
          }}
        >
          {t('nav.groups')}
          {location.pathname === '/groups' && (
            <span
              style={{
                position: 'absolute',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 28,
                height: 3,
                borderRadius: 3,
                background: 'var(--brand)',
              }}
            />
          )}
        </button>

        {/* Icon-only: Search */}
        <button
          onClick={() => navigate('/search')}
          aria-label={t('nav.search')}
          aria-current={location.pathname === '/search'}
          style={{
            width: 44,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: location.pathname === '/search' ? 'var(--brand)' : 'var(--text-muted)',
          }}
        >
          <Search size={18} />
        </button>
      </div>

      {activeTag && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderTop: '1px solid var(--border)' }}>
          <button
            type="button"
            onClick={clearTag}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '3px 10px 3px 10px',
              borderRadius: 'var(--radius-pill)',
              border: '1.5px solid var(--brand)',
              background: 'var(--brand-soft)',
              color: 'var(--brand)',
              fontFamily: 'var(--font-sans)',
              fontWeight: 700,
              fontSize: 'var(--fs-xs)',
              cursor: 'pointer',
            }}
          >
            #{activeTag}
            <X size={12} style={{ marginInlineStart: 2 }} />
          </button>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-faint)' }}>
            {t('tags.filter_active')}
          </span>
        </div>
      )}
    </div>
  )
}
